"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { generateNames } from "@/lib/tool-kit/random-tools/character-names";

type NameStyle = "classic" | "modern" | "fantasy";

type Strings = { [K in keyof typeof CHARACTER_NAME_GENERATOR_STRINGS]: string };

const NAME_STYLES: { value: NameStyle; label: keyof Strings }[] = [
  { value: "classic", label: "styleClassic" },
  { value: "modern", label: "styleModern" },
  { value: "fantasy", label: "styleFantasy" },
];

const CLASSIC_FIRST_NAMES = [
  "Eleanor",
  "Arthur",
  "Margaret",
  "Henry",
  "Clara",
  "Walter",
  "Alice",
  "Edmund",
  "Florence",
  "George",
  "Harriet",
  "Frederick",
  "Rose",
  "Albert",
  "Beatrice",
  "Charles",
  "Dorothy",
  "Ernest",
  "Evelyn",
  "Francis",
  "Grace",
  "Harold",
  "Ida",
  "James",
  "Josephine",
  "Leonard",
  "Lillian",
  "Martin",
  "Mabel",
  "Nathaniel",
  "Nora",
  "Oliver",
  "Pearl",
  "Raymond",
  "Ruth",
  "Samuel",
  "Sylvia",
  "Theodore",
  "Vera",
  "Vincent",
  "Violet",
  "William",
  "Winifred",
  "Cecil",
  "Agnes",
  "Edgar",
  "Flora",
  "Hugh",
  "Irene",
  "Louis",
];

const MODERN_FIRST_NAMES = [
  "Aiden",
  "Ava",
  "Brooklyn",
  "Caleb",
  "Chloe",
  "Dylan",
  "Ella",
  "Ethan",
  "Finn",
  "Gianna",
  "Harper",
  "Hudson",
  "Isla",
  "Jaxon",
  "Jordan",
  "Kayla",
  "Kai",
  "Layla",
  "Liam",
  "Luna",
  "Mason",
  "Maya",
  "Mia",
  "Micah",
  "Noah",
  "Nova",
  "Olivia",
  "Owen",
  "Parker",
  "Peyton",
  "Quinn",
  "Riley",
  "Ryan",
  "Sadie",
  "Sawyer",
  "Skylar",
  "Sofia",
  "Tyler",
  "Willow",
  "Wyatt",
  "Zoe",
  "Zion",
  "Aubrey",
  "Bentley",
  "Camila",
  "Declan",
  "Emery",
  "Grayson",
  "Hazel",
  "Jade",
];

const FANTASY_FIRST_NAMES = [
  "Aelric",
  "Brynna",
  "Caladorn",
  "Daeryn",
  "Elowen",
  "Faelar",
  "Gwyneth",
  "Hadrian",
  "Isolde",
  "Jorah",
  "Kaelith",
  "Lyra",
  "Maelis",
  "Nyssa",
  "Orin",
  "Peregrine",
  "Quillon",
  "Ravena",
  "Sylvaris",
  "Thalindra",
  "Ulric",
  "Vaelin",
  "Wrenna",
  "Xanthe",
  "Yavanna",
  "Zephyrine",
  "Alaric",
  "Brialla",
  "Corvyn",
  "Draven",
  "Eirlys",
  "Fenwick",
  "Galadren",
  "Hesper",
  "Ithildin",
  "Kestrel",
  "Lorien",
  "Morwenna",
  "Nimriel",
  "Oberon",
  "Rowanna",
  "Selwyn",
  "Tamsin",
  "Theron",
  "Vespera",
  "Wystan",
  "Ysolde",
  "Zarek",
  "Elandra",
  "Faramond",
];

export const COMMON_LAST_NAMES = [
  "Anderson",
  "Bailey",
  "Bennett",
  "Brooks",
  "Campbell",
  "Carter",
  "Chambers",
  "Clarke",
  "Coleman",
  "Collins",
  "Cooper",
  "Cruz",
  "Curtis",
  "Dawson",
  "Diaz",
  "Dixon",
  "Douglas",
  "Ellis",
  "Ferguson",
  "Fletcher",
  "Foster",
  "Franklin",
  "Gardner",
  "Graham",
  "Grant",
  "Greene",
  "Griffin",
  "Harper",
  "Harrison",
  "Hayes",
  "Henderson",
  "Holloway",
  "Hughes",
  "Jennings",
  "Keller",
  "Lawson",
  "Marshall",
  "Mason",
  "Mitchell",
  "Monroe",
  "Morgan",
  "Murphy",
  "Norris",
  "Palmer",
  "Parker",
  "Patterson",
  "Pearson",
  "Porter",
  "Reyes",
  "Reynolds",
  "Richardson",
  "Robinson",
  "Sanders",
  "Shepherd",
  "Sullivan",
  "Turner",
  "Vaughn",
  "Walsh",
  "Watson",
  "Webster",
];

export const FANTASY_LAST_NAMES = [
  "Ashgrove",
  "Blackbriar",
  "Brightwater",
  "Cinderfell",
  "Dawnbrook",
  "Duskmere",
  "Emberwood",
  "Frostwhisper",
  "Gladewalker",
  "Grimshaw",
  "Hallowmere",
  "Ironvale",
  "Larkspur",
  "Moonshadow",
  "Nightbloom",
  "Oakhollow",
  "Quicksilver",
  "Ravencrest",
  "Shadowbrook",
  "Silverbrook",
  "Snowmantle",
  "Stargazer",
  "Stormwood",
  "Sunhollow",
  "Thistledown",
  "Thornfield",
  "Wildersong",
  "Windmere",
  "Winterbourne",
  "Wolfsbane",
];

export const FIRST_NAME_POOLS: Record<NameStyle, string[]> = {
  classic: CLASSIC_FIRST_NAMES,
  modern: MODERN_FIRST_NAMES,
  fantasy: FANTASY_FIRST_NAMES,
};

const MIN_COUNT = 1;
const MAX_COUNT = 20;

interface NameChip {
  id: string;
  value: string;
}

function clampCount(value: number): number {
  if (Number.isNaN(value)) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.floor(value)));
}

/** English strings. A translated content file overrides these through `ui`. */
export const CHARACTER_NAME_GENERATOR_STRINGS = {
  nameStyle: "Name style",
  styleClassic: "Classic",
  styleModern: "Modern",
  styleFantasy: "Fantasy",
  howManyNames120: "How many names (1–20)",
  generateNames: "Generate Names",
  copied: "Copied!",
  copyAll: "Copy All",
  yourCharacterNames: "Your character names",
  namesAreRandomlyCombinedFrom:
    "Names are randomly combined from curated built-in lists of first and last names. Any resemblance to a real person is coincidental.",
} as const;

export default function CharacterNameGeneratorWidget() {
  const [style, setStyle] = useState<NameStyle>("classic");
  const [count, setCount] = useState(5);
  const [names, setNames] = useState<NameChip[]>([]);
  const [copied, setCopied] = useState(false);

  function handleGenerate() {
    setNames(
      generateNames(
        FIRST_NAME_POOLS[style],
        style === "fantasy" ? FANTASY_LAST_NAMES : COMMON_LAST_NAMES,
        count
      ).map((value) => ({
        id: crypto.randomUUID(),
        value,
      }))
    );
    setCopied(false);
  }

  function handleCopyAll() {
    if (names.length === 0) {
      return;
    }
    const text = names.map((n) => n.value).join("\n");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const strings = useToolUi(CHARACTER_NAME_GENERATOR_STRINGS);

  return (
    <>
      {/* Style */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.nameStyle}
        </p>
        <div className="flex flex-wrap gap-3">
          {NAME_STYLES.map((s) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={s.value}
            >
              <input
                checked={style === s.value}
                className="accent-primary"
                name="name-style"
                onChange={() => setStyle(s.value)}
                type="radio"
                value={s.value}
              />
              {strings[s.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Count */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="name-count"
        >
          {strings.howManyNames120}
        </label>
        <input
          className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="name-count"
          max={MAX_COUNT}
          min={MIN_COUNT}
          onChange={(e) => setCount(clampCount(Number(e.target.value)))}
          type="number"
          value={count}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateNames}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={names.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
      </div>
      {/* Results */}
      {names.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.yourCharacterNames}
          </h2>
          <div className="flex flex-wrap gap-2">
            {names.map((n) => (
              <span
                className="rounded-full border border-border bg-card px-4 py-1.5 text-foreground text-sm"
                key={n.id}
              >
                {n.value}
              </span>
            ))}
          </div>
        </section>
      )}
      <p className="text-muted-foreground text-xs leading-relaxed">
        {strings.namesAreRandomlyCombinedFrom}
      </p>
    </>
  );
}
